import { useEffect, useState } from 'react';
import { useAppSelector } from '../../../../app/hooks';
import { getTabLockStatus } from '../../../../store/investor/service';
import { getAllTaskTrackerData } from '../../../../store/investor/investorApiHelper';
import { getSecondaryTabIds, isTabLockedForColumn } from '../../../../store/investor/taskTrackerResolution';
import type { TaskTrackerMap } from '../../../../store/investor/taskTrackerResolution';
import { JAVA_API_STATUS } from '../../../../utils/axios-interceptor';
import { TabModule } from '../../../../utils/constants/commonConstants';

const fetchRelatedPartyLockMap = async (orgId: string): Promise<Record<string, { isLocked?: boolean }>> => {
    const response = await getTabLockStatus({ tabsList: [TabModule.RELATED_PARTY], assesseeOrgId: orgId });
    if (response?.data?.responseData?.responseCode !== JAVA_API_STATUS.SUCCESS) throw new Error('Tab lock status unavailable');
    return response.data?.dashboardTabLockConfigMap ?? {};
};

// noToast: this runs on every company page, so a failure must not raise an error toast. The helper
// settles on success or an explicit error only — an unrecognised responseCode never settles, which
// leaves the lock closed.
const fetchTaskTracker = (orgId: string): Promise<TaskTrackerMap> =>
    new Promise((resolve, reject) => {
        getAllTaskTrackerData({ orgId }, { onSuccess: resolve, onError: reject }, true);
    });

/**
 * Whether the Related Parties tab is locked for `orgId`, read straight from the two services — never
 * through `state.investor.tabLockStatus` / `taskTrackerMap`: those are one org-unkeyed slot shared with
 * the sidebar, and dispatching `fetchTabLockStatus` would cancel its in-flight request. Fails closed
 * (locked) while pending, on any error, and when the answer belongs to another org. `refreshKey`
 * re-reads the lock, so each drawer open sees the current state.
 */
export function useRelatedPartiesLock(orgId: string | undefined, refreshKey: number): boolean {
    const [answer, setAnswer] = useState<{
        orgId: string;
        refreshKey: number;
        lockMap: Record<string, { isLocked?: boolean }>;
        taskTrackerMap: TaskTrackerMap;
    }>();
    const tabs: { id: string; category?: string }[] | undefined = useAppSelector((state) => (state.investor.config as any)?.tabs);

    useEffect(() => {
        if (!orgId) return undefined;
        let cancelled = false;
        Promise.all([fetchRelatedPartyLockMap(orgId), fetchTaskTracker(orgId)])
            .then(([lockMap, taskTrackerMap]) => {
                if (!cancelled) setAnswer({ orgId, refreshKey, lockMap, taskTrackerMap });
            })
            .catch((error) => {
                console.error('Error fetching related parties lock', error);
            });
        return () => {
            cancelled = true;
        };
    }, [orgId, refreshKey]);

    // Fail closed: without the dashboard config we cannot tell a secondary tab from a primary one.
    if (!orgId || !answer || answer.orgId !== orgId || answer.refreshKey !== refreshKey) return true;
    if (!Array.isArray(tabs) || tabs.length === 0) return true;
    const secondaryModuleList = getSecondaryTabIds(tabs);
    return isTabLockedForColumn(TabModule.RELATED_PARTY, answer.lockMap, answer.taskTrackerMap, secondaryModuleList);
}
